/**
 * Video Preview Player Component (canvas-v2)
 *
 * Displays the current frame at the playhead position.
 * Handles client-side video sequencing across multiple clips.
 *
 * Architecture: premounts one <video> element per clip for zero-gap transitions.
 * - Each element uses a blob URL from the preloader (no network fetch at transition time)
 * - Eager pre-seek to trimStart on blob load and trim change
 * - Play-before-pause ordering at transitions: start new element BEFORE pausing old
 * - Only active clip's element is visible; all others are hidden
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react'
import type { TimelineClip } from './types'
import { audioFadeGain } from './audioFades'
import type { UseClipPreloaderReturn } from './useClipPreloader'
import { getAssetUrl } from '../../../../api/useAssetUrl'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useAuthenticatedMediaUrl } from '../../../../hooks/useAuthenticatedMediaUrl'
import createDebug from 'debug'
import { createNadDebug } from '../../../../utils/debugLog'
import { useTranslation } from 'react-i18next'

const debug = createDebug('dw:timeline-playback:clip-transition')
const playbackLog = createNadDebug('dw:timeline-overhaul:playback')
const fadeLog = createNadDebug('dw:video-editor-improvements')

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext
  }
}

interface VideoPreviewPlayerProps {
  onSourceSize?: (width: number, height: number) => void
  textLayer?: React.ReactNode
  textAspect?: number

  surface?: 'canvas' | 'editor'
  ownerNodeId?: string
  /** Current clips on timeline */
  clips: TimelineClip[]

  /** Current playhead position (seconds) */
  playheadPosition: number
  /** Shared RAF clock; media switches independently of throttled React updates. */
  playheadPositionRef?: MutableRefObject<number>

  /** Is video currently playing */
  isPlaying: boolean

  /** Callback when video ends */
  onVideoEnd?: () => void

  /** Preloader providing blob URLs for all clips */
  preloader: UseClipPreloaderReturn

  /**
   * When true, every <video> is forced to `muted=true` regardless of
   * `clip.muted`. The canvas preview uses this for its global mute toggle so
   * the modal-owned per-clip mute is not mutated.
   */
  globalMuted?: boolean

  /** Hide the built-in clip-name overlay (the host renders its own). */
  hideClipBadge?: boolean

  /**
   * RFC-0651 #11: indices of video tracks that should be silenced. When
   * the active clip's trackIndex is in this set, its embedded audio is
   * muted (the <video> element keeps playing, only audio is suppressed).
   */
  mutedVideoTracks?: Set<number>
}

export interface TimelineVideoPreviewPlayerHandle {
  resumeAudioContexts: () => void
}

interface ClipWithTiming extends TimelineClip {
  /** Start time in final timeline (seconds) */
  timelineStart: number

  /** End time in final timeline (seconds) */
  timelineEnd: number
}

interface PreviewVideoElementProps {
  clip: ClipWithTiming
  isActive: boolean
  blobUrl: string | null
  setVideoRef: (clipId: string) => (el: HTMLVideoElement | null) => void
  onLoadedData: (clip: ClipWithTiming) => void
  onError: (clip: ClipWithTiming, hasSource: boolean) => void
  globalMuted?: boolean
  /** RFC-0651: composed track-mute + per-clip mute. */
  effectivelyMuted?: boolean
  registerAudioResume: (clipId: string, resume: (() => void) | null) => void
}

function PreviewVideoElement({
  clip,
  isActive,
  blobUrl,
  setVideoRef,
  onLoadedData,
  onError,
  globalMuted = false,
  effectivelyMuted = false,
  registerAudioResume,
}: PreviewVideoElementProps) {
  const projectId = useProjectId()
  const baseUrl = useMemo(() => {
    if (!projectId || !clip.sourcePath) return null
    return getAssetUrl(projectId, clip.sourcePath)
  }, [projectId, clip.sourcePath])
  const directUrl = useAuthenticatedMediaUrl(baseUrl, clip.sourcePath)
  const sourceUrl = blobUrl ?? directUrl ?? undefined

  // Local ref so we can imperatively sync volume (HTMLMediaElement.volume is
  // not a React attribute — it must be set on the DOM element directly).
  const localVideoRef = useRef<HTMLVideoElement | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null)
  const gainRef = useRef<GainNode | null>(null)
  const setRef = useCallback(
    (el: HTMLVideoElement | null) => {
      localVideoRef.current = el
      setVideoRef(clip.id)(el)
    },
    [clip.id, setVideoRef],
  )

  const resumeAudioContext = useCallback(() => {
    const ctx = audioCtxRef.current
    if (!ctx || ctx.state === 'closed') return
    void ctx.resume().catch(() => {
      /* autoplay policy can still reject outside a user gesture */
    })
  }, [])

  const ensureBoostAudioGraph = useCallback((el: HTMLVideoElement): GainNode | null => {
    if (gainRef.current) {
      resumeAudioContext()
      return gainRef.current
    }
    try {
      const AudioContextCtor = window.AudioContext || window.webkitAudioContext
      if (!AudioContextCtor) {
        debug('webaudio-unsupported: clipId=%s', clip.id)
        return null
      }
      const ctx = new AudioContextCtor()
      const source = ctx.createMediaElementSource(el)
      const gain = ctx.createGain()
      source.connect(gain).connect(ctx.destination)
      audioCtxRef.current = ctx
      sourceRef.current = source
      gainRef.current = gain
      resumeAudioContext()
      return gain
    } catch (err) {
      debug('webaudio-boost-setup-failed: clipId=%s err=%o', clip.id, err)
      return null
    }
  }, [clip.id, resumeAudioContext])

  // Use native media-element audio for normal 0..1 video clip volume. Only
  // capture into WebAudio for explicit >100% boost, where HTMLMediaElement
  // cannot represent the requested amplitude.
  useEffect(() => {
    const el = localVideoRef.current
    if (!el) return
    const muted = globalMuted || effectivelyMuted
    const targetVolume = Math.max(0, clip.volume)
    const gain = targetVolume > 1 || gainRef.current ? ensureBoostAudioGraph(el) : null
    el.muted = muted
    if (gain) {
      el.volume = 1
      gain.gain.value = muted ? 0 : targetVolume
    } else {
      const nativeVolume = Math.min(1, targetVolume)
      if (Math.abs(el.volume - nativeVolume) > 0.001) {
        el.volume = nativeVolume
      }
    }
    debug(
      'clip-audio-applied: clipId=%s clipVolume=%f muted=%s nativeVolume=%f viaBoostGain=%s',
      clip.id,
      clip.volume,
      muted,
      Math.min(1, targetVolume),
      Boolean(gain),
    )
  }, [clip.id, clip.volume, clip.muted, clip.audioFadeInS, clip.audioFadeOutS, globalMuted, effectivelyMuted, ensureBoostAudioGraph])

  useEffect(() => {
    registerAudioResume(clip.id, resumeAudioContext)
    return () => registerAudioResume(clip.id, null)
  }, [clip.id, registerAudioResume, resumeAudioContext])

  useEffect(() => {
    if (!isActive || (!(clip.audioFadeInS ?? 0) && !(clip.audioFadeOutS ?? 0))) return
    let frame: number
    const tick = () => {
      const el = localVideoRef.current
      if (el) {
        const amplitude = clip.volume * audioFadeGain(clip, (el.currentTime - clip.trimStart) / clip.speed)
        if (gainRef.current) gainRef.current.gain.value = globalMuted || effectivelyMuted ? 0 : amplitude
        else el.volume = Math.min(1, Math.max(0, amplitude))
      }
      frame = requestAnimationFrame(tick)
    }
    tick()
    return () => cancelAnimationFrame(frame)
  }, [clip, isActive, globalMuted, effectivelyMuted])

  useEffect(() => {
    return () => {
      try {
        sourceRef.current?.disconnect()
      } catch {
        /* noop */
      }
      try {
        gainRef.current?.disconnect()
      } catch {
        /* noop */
      }
      audioCtxRef.current?.close().catch(() => {
        /* noop */
      })
      audioCtxRef.current = null
      sourceRef.current = null
      gainRef.current = null
    }
  }, [])

  return (
    <video
      key={clip.id}
      data-clip-id={clip.id}
      ref={setRef}
      src={sourceUrl}
      className="absolute inset-0 w-full h-full object-contain"
      style={{
        opacity: isActive ? 1 : 0,
        filter: `brightness(${1 + (clip.adjustments?.brightness ?? 0)}) contrast(${clip.adjustments?.contrast ?? 1}) saturate(${clip.adjustments?.saturation ?? 1})`,
        transform: `translate(${(clip.adjustments?.position_x ?? 0) * 50}%, ${(clip.adjustments?.position_y ?? 0) * 50}%) rotate(${clip.adjustments?.rotation ?? 0}deg) scale(${(clip.adjustments?.scale ?? 1) * (clip.adjustments?.flip_horizontal ? -1 : 1)}, ${(clip.adjustments?.scale ?? 1) * (clip.adjustments?.flip_vertical ? -1 : 1)})`,
        pointerEvents: isActive ? 'auto' : 'none',
        zIndex: isActive ? 1 : 0,
      }}
      onLoadedData={() => {
        const el = localVideoRef.current
        if (el) {
          const targetVolume = Math.max(0, clip.volume)
          const gain = targetVolume > 1 || gainRef.current ? ensureBoostAudioGraph(el) : null
          el.muted = globalMuted || effectivelyMuted
          if (gain) {
            el.volume = 1
            gain.gain.value = globalMuted || effectivelyMuted ? 0 : targetVolume
          } else {
            el.volume = Math.min(1, targetVolume)
          }
        }
        onLoadedData(clip)
      }}
      onError={() => onError(clip, Boolean(sourceUrl))}
      playsInline
      preload="auto"
      muted={globalMuted || effectivelyMuted}
    />
  )
}

export const VideoPreviewPlayer = forwardRef<TimelineVideoPreviewPlayerHandle, VideoPreviewPlayerProps>(
function VideoPreviewPlayer({
  textLayer, textAspect, onSourceSize,
  surface = 'editor',
  ownerNodeId,
  clips,
  playheadPosition,
  playheadPositionRef,
  isPlaying,
  preloader,
  globalMuted = false,
  hideClipBadge = false,
  mutedVideoTracks,
}: VideoPreviewPlayerProps, ref) {
  const { t } = useTranslation('canvas')
  const projectId = useProjectId()
  const logContext = useRef({ project_id: projectId, node_id: ownerNodeId, surface,
    trace_id: `timeline-preview:${projectId}:${ownerNodeId ?? 'preview'}:${surface}` })
  logContext.current = { project_id: projectId, node_id: ownerNodeId, surface,
    trace_id: `timeline-preview:${projectId}:${ownerNodeId ?? 'preview'}:${surface}` }
  const videoRefsMap = useRef<Record<string, HTMLVideoElement | null>>({})
  const audioResumeMapRef = useRef<Map<string, () => void>>(new Map())
  const [activeClipId, setActiveClipId] = useState<string | null>(null)
  const activeClipIdRef = useRef<string | null>(null)
  const previousPlayheadRef = useRef(playheadPosition)
  const fadeOverlayRef = useRef<HTMLDivElement>(null)
  const [mediaError, setMediaError] = useState<string | null>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const [viewport, setViewport] = useState({ width: 0, height: 0 })
  const [frameAspect, setFrameAspect] = useState(16 / 9)
  useEffect(() => {
    const element = viewportRef.current
    if (!element) return
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setViewport({ width: entry.contentRect.width, height: entry.contentRect.height })
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])
  const pictureAspect = textAspect ?? frameAspect
  const frameWidth = Math.min(viewport.width, viewport.height * pictureAspect)

  // Track pre-seek state so we don't re-seek unnecessarily
  const preSeekMapRef = useRef<Map<string, number>>(new Map())

  // Compute clip timings (start/end times in the final timeline)
  const clipsWithTiming: ClipWithTiming[] = useMemo(() => {
    return [...clips]
      .sort((a, b) => a.trackIndex - b.trackIndex || a.startOffset - b.startOffset || a.order - b.order)
      .map((clip) => ({
        ...clip,
        timelineStart: clip.startOffset,
        timelineEnd: clip.startOffset + clip.effectiveDuration,
      }))
  }, [clips])

  // Find which clip should be shown at current playhead position
  const activeClip = useMemo(() => {
    return clipsWithTiming.find(
      (clip) => playheadPosition >= clip.timelineStart && playheadPosition < clip.timelineEnd
    )
  }, [clipsWithTiming, playheadPosition])

  // Fade overlay opacity (Issue 4 — video transition preview).
  // Black-to-clip ramp for fade-in (1 → 0 across first fadeInS seconds of clip).
  // Clip-to-black ramp for fade-out (0 → 1 across last fadeOutS seconds of clip).
  const fadeOpacity = useMemo(() => {
    if (!activeClip) return 0
    const t = playheadPosition - activeClip.timelineStart
    const eff = activeClip.effectiveDuration
    const fadeIn = Math.max(0, Math.min(eff, activeClip.fadeInS ?? 0))
    const fadeOut = Math.max(0, Math.min(eff, activeClip.fadeOutS ?? 0))
    const inOpacity = fadeIn > 0 && t < fadeIn ? 1 - t / fadeIn : 0
    const outOpacity =
      fadeOut > 0 && t > eff - fadeOut ? (t - (eff - fadeOut)) / fadeOut : 0
    return Math.max(0, Math.min(1, Math.max(inOpacity, outOpacity)))
  }, [activeClip, playheadPosition])

  // Throttle fade-overlay logging to avoid spamming nad-debug.log during scrub/play.
  const lastFadeLogRef = useRef(0)
  useEffect(() => {
    if (!activeClip) return
    const fadeIn = activeClip.fadeInS ?? 0
    const fadeOut = activeClip.fadeOutS ?? 0
    if (fadeIn === 0 && fadeOut === 0) return
    const now = Date.now()
    if (now - lastFadeLogRef.current < 250) return
    lastFadeLogRef.current = now
    fadeLog({
      event: 'fade-overlay-render',
      activeClipId: activeClip.id,
      t: Number((playheadPosition - activeClip.timelineStart).toFixed(3)),
      fadeOpacity: Number(fadeOpacity.toFixed(3)),
      fadeInS: fadeIn,
      fadeOutS: fadeOut,
    })
  }, [activeClip, playheadPosition, fadeOpacity])

  // Current clip index for display
  const currentClipIndex = activeClip
    ? clipsWithTiming.findIndex((c) => c.id === activeClip.id)
    : null

  // RFC-0651 #11: compose per-clip mute with per-track mute. When the
  // active clip's trackIndex is in `mutedVideoTracks`, the embedded audio
  // is silenced. We forward this as an OR with the existing per-clip flag
  // through a clipId→effective-mute map so PreviewVideoElement can render
  // declaratively.
  const effectiveMutedByClipId = useMemo(() => {
    const out = new Map<string, boolean>()
    for (const clip of clipsWithTiming) {
      const trackMuted = mutedVideoTracks?.has(clip.trackIndex) ?? false
      out.set(clip.id, clip.muted || trackMuted)
    }
    return out
  }, [clipsWithTiming, mutedVideoTracks])

  // Ref callback for collecting video element refs
  const setVideoRef = useCallback((clipId: string) => (el: HTMLVideoElement | null) => {
    videoRefsMap.current[clipId] = el
  }, [])

  const registerAudioResume = useCallback((clipId: string, resume: (() => void) | null) => {
    if (resume) {
      audioResumeMapRef.current.set(clipId, resume)
    } else {
      audioResumeMapRef.current.delete(clipId)
    }
  }, [])

  useImperativeHandle(ref, () => ({
    resumeAudioContexts: () => {
      for (const resume of audioResumeMapRef.current.values()) {
        resume()
      }
    },
  }), [])

  const latest = useRef({ clipsWithTiming, playheadPosition, playheadPositionRef, isPlaying })
  latest.current = { clipsWithTiming, playheadPosition, playheadPositionRef, isPlaying }

  const syncMedia = useCallback(() => {
    const current = latest.current
    const position = current.isPlaying
      ? current.playheadPositionRef?.current ?? current.playheadPosition
      : current.playheadPosition
    const clip = current.clipsWithTiming.find((item) =>
      position >= item.timelineStart && position < item.timelineEnd)
    const previousId = activeClipIdRef.current
    const nextId = clip?.id ?? null
    const switched = previousId !== nextId
    const seeking = !current.isPlaying || Math.abs(position - previousPlayheadRef.current) > 0.25
    const video = clip ? videoRefsMap.current[clip.id] : null

    if (clip && video) {
      const sourceTime = clip.trimStart + Math.max(0, position - clip.timelineStart) * clip.speed
      video.playbackRate = clip.speed
      const primedBoundary = switched && current.isPlaying && !seeking
        && position - clip.timelineStart < 0.1 && Math.abs(video.currentTime - clip.trimStart) < 0.025
      // At an ordinary boundary, play the already-decoded first frame. Seeking
      // by a few milliseconds here discards that frame and stalls the decoder.
      if (!primedBoundary && (switched || seeking) && Math.abs(video.currentTime - sourceTime) > 0.025) {
        video.currentTime = sourceTime
      }
      if (current.isPlaying && video.paused) {
        void video.play().catch((error) => {
          if (error instanceof DOMException && error.name === 'AbortError') return
          setMediaError(error instanceof Error ? error.message : String(error))
        })
      } else if (!current.isPlaying && !video.paused) {
        video.pause()
      }
    }

    if (switched) {
      // Update visibility in this animation frame, without a second React commit.
      // Hide the outgoing frame before pre-seeking it for subsequent playback.
      for (const [id, element] of Object.entries(videoRefsMap.current)) {
        if (!element) continue
        element.style.opacity = id === nextId ? '1' : '0'
        element.style.zIndex = id === nextId ? '1' : '0'
        if (id !== nextId) {
          element.pause()
          const inactive = current.clipsWithTiming.find((item) => item.id === id)
          if (inactive && element.readyState >= 1 && Math.abs(element.currentTime - inactive.trimStart) > 0.025) {
            element.currentTime = inactive.trimStart
          }
        }
      }
      activeClipIdRef.current = nextId
      setActiveClipId(nextId)
      playbackLog({ event: 'preview-active-clip-changed', from_clip_id: previousId, clip_id: nextId,
        ...logContext.current, isPlaying: current.isPlaying, reason: current.isPlaying && !seeking ? 'playback' : 'seek-or-load',
        playhead: position, boundaryDelayMs: clip && current.isPlaying && !seeking ? (position - clip.timelineStart) * 1000 : null,
        readyState: video?.readyState ?? null,
        ready_state_name: video ? ['HAVE_NOTHING', 'HAVE_METADATA', 'HAVE_CURRENT_DATA', 'HAVE_FUTURE_DATA', 'HAVE_ENOUGH_DATA'][video.readyState] : null,
        speed: clip?.speed ?? null })
    }
    if (fadeOverlayRef.current) {
      const t = clip ? position - clip.timelineStart : 0
      const fadeIn = Math.min(clip?.effectiveDuration ?? 0, clip?.fadeInS ?? 0)
      const fadeOut = Math.min(clip?.effectiveDuration ?? 0, clip?.fadeOutS ?? 0)
      const opacity = Math.max(0, Math.min(1, Math.max(
        fadeIn > 0 ? 1 - t / fadeIn : 0,
        fadeOut > 0 && clip ? (t - (clip.effectiveDuration - fadeOut)) / fadeOut : 0,
      )))
      fadeOverlayRef.current.style.opacity = String(opacity)
      fadeOverlayRef.current.style.backgroundColor = clip?.fadeColor ?? 'black'
      fadeOverlayRef.current.dataset.fadeOpacity = opacity.toFixed(3)
    }
    previousPlayheadRef.current = position
  }, [])

  const syncFrameAspect = useCallback(() => {
    const first = latest.current.clipsWithTiming[0]
    const video = first && videoRefsMap.current[first.id]
    if (video?.videoWidth && video.videoHeight) {
      setFrameAspect(video.videoWidth / video.videoHeight)
      onSourceSize?.(video.videoWidth, video.videoHeight)
    }
  }, [onSourceSize])
  useEffect(syncFrameAspect, [clipsWithTiming, syncFrameAspect])

  const handleLoadedData = useCallback((clip: ClipWithTiming) => {
    const video = videoRefsMap.current[clip.id]
    if (!video) return
    setMediaError(null)
    syncFrameAspect()
    const current = latest.current
    const position = current.isPlaying
      ? current.playheadPositionRef?.current ?? current.playheadPosition
      : current.playheadPosition
    const relativeTime = clip.id === activeClipIdRef.current
      ? Math.max(0, Math.min(clip.effectiveDuration, position - clip.timelineStart)) : 0
    video.currentTime = clip.trimStart + relativeTime * clip.speed
    preSeekMapRef.current.set(clip.id, clip.trimStart)
    playbackLog({ event: 'source-loaded-and-seek-requested', ...logContext.current, clip_id: clip.id, sourcePath: clip.sourcePath,
      sourceTime: video.currentTime, readyState: video.readyState })
    syncMedia()
  }, [syncMedia, syncFrameAspect])

  useEffect(() => {
    for (const clip of clipsWithTiming) {
      if (clip.id === activeClipIdRef.current) continue
      const video = videoRefsMap.current[clip.id]
      if (video && video.readyState >= 1 && preSeekMapRef.current.get(clip.id) !== clip.trimStart) {
        video.currentTime = clip.trimStart
        preSeekMapRef.current.set(clip.id, clip.trimStart)
      }
    }
    syncMedia()
  }, [clipsWithTiming, playheadPosition, isPlaying, syncMedia])

  useEffect(() => {
    if (!isPlaying) return
    let frame: number
    const tick = () => {
      syncMedia()
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [isPlaying, syncMedia])

  const handleVideoError = useCallback((clip: ClipWithTiming, hasSource: boolean) => {
    if (!hasSource) return
    const error = videoRefsMap.current[clip.id]?.error
    const message = error?.message || 'Unable to load video'
    setMediaError(`${clip.label}: ${message}`)
    playbackLog({ event: 'media-error', ...logContext.current, clipId: clip.id, sourcePath: clip.sourcePath,
      code: error?.code ?? null, message })
  }, [])

  return (
    <div ref={viewportRef} className="relative flex items-center justify-center w-full h-full bg-black rounded-lg overflow-hidden border border-border">
      {mediaError && <div role="alert" className="absolute inset-x-3 top-3 z-20 rounded-lg border border-destructive bg-surface-0 p-3 text-sm text-destructive">{mediaError}</div>}
      {clips.length > 0 || textLayer ? (
        <>
          {/* Premount one <video> per clip */}
          <div className="relative overflow-hidden" data-testid="timeline-picture-frame"
            style={frameWidth > 0 ? { width: frameWidth, height: frameWidth / pictureAspect } : { width: '100%', height: '100%' }}>
            {clipsWithTiming.map((clip) => {
              const blobUrl = preloader.getBlobUrl(clip.sourcePath)
              const isActive = clip.id === activeClipId

              return (
                <PreviewVideoElement
                  key={`${clip.id}:${clip.sourcePath}`}
                  clip={clip}
                  isActive={isActive}
                  blobUrl={blobUrl}
                  setVideoRef={setVideoRef}
                  onLoadedData={handleLoadedData}
                  onError={handleVideoError}
                  globalMuted={globalMuted}
                  effectivelyMuted={effectiveMutedByClipId.get(clip.id) ?? clip.muted}
                  registerAudioResume={registerAudioResume}
                />
              )
            })}

            {/* Fade-to-black overlay (Issue 4). Sits above the active <video>
                element (z=1) but below the clip badge (z=10). Opacity is
                derived per render from playhead vs active clip's fade region. */}
            <div
              className="absolute inset-0 pointer-events-none bg-black"
              style={{ opacity: fadeOpacity, zIndex: 5 }}
              ref={fadeOverlayRef}
              data-testid="timeline-preview-fade-overlay"
              data-fade-opacity={fadeOpacity.toFixed(3)}
              aria-hidden="true"
            />
            {textLayer}
          </div>

          {/* Preloader progress indicator */}
          {!preloader.isReady && preloader.loadingCount > 0 && (
            <div className="absolute bottom-3 right-3 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-lg border border-white/10 z-10">
              <p className="text-text-muted text-xxs">
                {t('videoPreviewPlayer.preloadingRemaining', { count: preloader.loadingCount })}
              </p>
            </div>
          )}

          {/* Clip info overlay */}
          {!hideClipBadge && activeClip && (
            <div className="absolute top-3 left-3 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-lg border border-white/10 z-10">
              <p className="text-foreground text-xs font-medium">{activeClip.label}</p>
              <p className="text-text-muted text-xxs">
                {t('videoPreviewPlayer.clipOfCount', { index: (currentClipIndex ?? 0) + 1, total: clips.length })}
                {activeClip.speed !== 1.0 && ` | ${t('videoPreviewPlayer.speedLabel', { speed: activeClip.speed })}`}
              </p>
            </div>
          )}
        </>
      ) : (
        /* No clips */
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <svg className="w-16 h-16 text-foreground/20 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-foreground/30 text-sm">{t('videoPreviewPlayer.noClipsOnTimeline')}</p>
          </div>
        </div>
      )}
    </div>
  )
})
